import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

// Components
import ErrorBoundary from './components/ErrorBoundary';
import AccessGate from './components/AccessGate';
import WelcomeOnboarding from './components/WelcomeOnboarding';
import Navigation from './components/Navigation';
import BookVision from './components/BookVision';
import StructureOutline from './components/StructureOutline';
import ProjectScope from './components/ProjectScope';
import ChapterKickstart from './components/ChapterKickstart';
import SummaryExport from './components/SummaryExport';
import MotivationalQuotes from './components/MotivationalQuotes';
import ProgressCelebration from './components/ProgressCelebration';

// Utils
import storage from './utils/localStorage';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [appData, setAppData] = useState(null);

  // Initialize app
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Small delay to prevent flash
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Get data with fallback
        let data;
        try {
          data = storage.getData();
        } catch (error) {
          console.error('Storage getData failed, resetting:', error);
          storage.reset();
          data = storage.getData();
        }

        // Ensure data is valid
        if (!data || typeof data !== 'object') {
          console.warn('Invalid data structure, resetting storage');
          storage.reset();
          data = storage.getData();
        }

        setAppData(data);
        setHasAccess(data?.access?.granted || false);
        setShowOnboarding((data?.access?.granted || false) && !(data?.onboarding?.completed || false));
        
      } catch (error) {
        console.error('App initialization error:', error);
        // Reset on critical error and set safe defaults
        try {
          storage.reset();
          const freshData = storage.getData();
          setAppData(freshData);
          setHasAccess(false);
          setShowOnboarding(false);
        } catch (resetError) {
          console.error('Failed to reset storage:', resetError);
          // Last resort - set minimal safe state
          setAppData({
            access: { granted: false },
            onboarding: { completed: false, authorName: '' },
            bookVision: {},
            structure: {},
            scope: {},
            kickstart: {},
            progress: { completedSections: [] }
          });
          setHasAccess(false);
          setShowOnboarding(false);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  const handleAccessGranted = () => {
    try {
      storage.updateSection('access', { granted: true, date: new Date().toISOString() });
      const updatedData = storage.getData();
      setAppData(updatedData);
      setHasAccess(true);
      setShowOnboarding(true);
    } catch (error) {
      console.error('Error granting access:', error);
    }
  };

  const handleOnboardingComplete = (onboardingData) => {
    try {
      storage.updateSection('onboarding', { ...onboardingData, completed: true });
      const updatedData = storage.getData();
      setAppData(updatedData);
      setShowOnboarding(false);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  const updateAppData = (section, data) => {
    try {
      storage.updateSection(section, data);
      const updatedData = storage.getData();
      setAppData(updatedData);
    } catch (error) {
      console.error('Error updating app data:', error);
    }
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-brand-blue/10 to-brand-orange/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-orange border-t-transparent mx-auto mb-4"></div>
          <p className="text-brand-blue font-medium">Loading your writing sanctuary...</p>
        </motion.div>
      </div>
    );
  }

  // Safety check - if appData is still null, show error
  if (!appData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-brand-blue/10 to-brand-orange/20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-brand-blue mb-4">Unable to load application data</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-brand-orange text-white px-6 py-2 rounded-lg"
          >
            Reload App
          </button>
        </div>
      </div>
    );
  }

  // Access gate
  if (!hasAccess) {
    return (
      <ErrorBoundary>
        <AccessGate onAccessGranted={handleAccessGranted} />
      </ErrorBoundary>
    );
  }

  // Onboarding
  if (showOnboarding) {
    return (
      <ErrorBoundary>
        <WelcomeOnboarding onComplete={handleOnboardingComplete} />
      </ErrorBoundary>
    );
  }

  // Get the user's name from the stored data
  const userName = appData?.onboarding?.authorName || 'Author';

  // Main app
  return (
    <ErrorBoundary>
      <Router>
        <div className="min-h-screen bg-gradient-to-br from-cream via-brand-blue/10 to-brand-orange/20">
          <Navigation 
            completedSections={appData?.progress?.completedSections || []}
            authorName={userName}
          />
          
          <main className="pt-64 pb-8">
            <div className="max-w-4xl mx-auto px-4">
              <div className="md:pt-5">
                <MotivationalQuotes />
              </div>
              <ProgressCelebration 
                progress={storage.getProgress()}
                authorName={userName}
              />
              
              <AnimatePresence mode="wait">
                <Routes>
                  <Route path="/" element={<Navigate to="/book-vision" replace />} />
                  <Route 
                    path="/book-vision" 
                    element={
                      <BookVision 
                        data={appData.bookVision || {}}
                        onUpdate={(data) => updateAppData('bookVision', data)}
                        onComplete={() => storage.markSectionComplete('bookVision')}
                      />
                    } 
                  />
                  <Route 
                    path="/structure" 
                    element={
                      <StructureOutline 
                        data={appData.structure || {}}
                        onUpdate={(data) => updateAppData('structure', data)}
                        onComplete={() => storage.markSectionComplete('structure')}
                      />
                    } 
                  />
                  <Route 
                    path="/scope" 
                    element={
                      <ProjectScope 
                        data={appData.scope || {}}
                        onUpdate={(data) => updateAppData('scope', data)}
                        onComplete={() => storage.markSectionComplete('scope')}
                      />
                    } 
                  />
                  <Route 
                    path="/kickstart" 
                    element={
                      <ChapterKickstart 
                        data={appData.kickstart || {}}
                        onUpdate={(data) => updateAppData('kickstart', data)}
                        onComplete={() => storage.markSectionComplete('kickstart')}
                      />
                    } 
                  />
                  <Route 
                    path="/summary" 
                    element={
                      <SummaryExport 
                        allData={appData}
                        onComplete={() => storage.markSectionComplete('summary')}
                      />
                    } 
                  />
                </Routes>
              </AnimatePresence>
            </div>
          </main>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;